import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Outlet } from 'react-router-dom';
import { FiShield, FiEdit2 } from 'react-icons/fi';
import { FaPlus, FaMinus } from 'react-icons/fa';
import { IoIosRefresh } from 'react-icons/io';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { toast } from 'react-toastify';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import PageFooter from '../../molecules/PageFooter';
import { collateralApi, ApiError } from '../../../services/collateralApi';
import NewTooltip from '../../atoms/NewTooltip';
import DebtorContactModal from './DraweeValidationPageForm';
import {
    COLLATERAL_ROUTES,
    formatCurrency,
    formatDate,
    formatCpfCnpj,
    getReceivableStatusColor,
    type ReceivableStatus,
} from './collateralUtils';

const DV_STATUS_LABELS: Record<string, string> = {
    NOT_STARTED: 'Não iniciado', IN_VALIDATION: 'Em validação',
    FULLY_VALIDATED: 'Validado', PARTIALLY_VALIDATED: 'Validado parcial',
};
const DV_STATUS_COLORS: Record<string, string> = {
    NOT_STARTED: 'bg-gray-500 text-white', IN_VALIDATION: 'bg-yellow-500 text-white',
    FULLY_VALIDATED: 'bg-green-500 text-white', PARTIALLY_VALIDATED: 'bg-orange-500 text-white',
};

interface DebtorGroup {
    key: string;
    name: string;
    document: string;
    contactId?: string;
    contactStatus?: string;
    validationStatus?: string;
    history?: any[];
    titleCount: number;
    totalAmount: number;
    totalValidated: number;
    status: string;
    items: any[];
}

const ReceivablePageList = () => {
    const { validationId } = useParams<{ validationId: string }>();
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [sortField, setSortField] = useState('debtor');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [selectedDebtor, setSelectedDebtor] = useState<any>(null);
    const [apiData, setApiData] = useState<any>(null);
    const [allRows, setAllRows] = useState<any[]>([]);
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });

    const fetchData = useCallback(async () => {
        if (!validationId) return;
        setIsLoading(true);
        try {
            const result = await collateralApi.getValidationView(validationId, {
                pageSize: 1000,
                search: search || undefined,
            });
            setApiData(result);
            setAllRows(result?.items || []);
            setApiInfo({ version: result?.apiVersion || '', environment: result?.environment || '' });
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/access-denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [validationId, search]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    // Group by debtor
    const groups = useMemo(() => {
        const map = new Map<string, DebtorGroup>();
        allRows.forEach((r: any) => {
            const key = r.debtorDocument || r.debtorName;
            if (!map.has(key)) {
                map.set(key, { key, name: r.debtorName, document: r.debtorDocument, contactId: r.contactId, contactStatus: r.contactStatus, validationStatus: r.validationStatus, history: r.history, titleCount: 0, totalAmount: 0, totalValidated: 0, status: '', items: [] });
            }
            const g = map.get(key)!;
            g.titleCount++;
            g.totalAmount += r.amount;
            if (r.status === 'CONFIRMED') g.totalValidated += r.amount;
            g.items.push(r);
        });
        map.forEach(g => {
            g.status = g.validationStatus || 'NOT_STARTED';
        });
        const arr = Array.from(map.values());
        const dir = sortOrder === 'ASC' ? 1 : -1;
        arr.sort((a, b) => {
            switch (sortField) {
                case 'doc': return dir * (a.document || '').localeCompare(b.document || '');
                case 'titles': return dir * (a.titleCount - b.titleCount);
                case 'amount': return dir * (a.totalAmount - b.totalAmount);
                case 'validated': return dir * (a.totalValidated - b.totalValidated);
                case 'status': return dir * (a.status || '').localeCompare(b.status || '');
                default: return dir * a.name.localeCompare(b.name);
            }
        });
        return arr;
    }, [allRows, sortField, sortOrder]);

    const filteredGroups = useMemo(() => {
        if (!statusFilter) return groups;
        return groups.filter(g => g.status === statusFilter);
    }, [groups, statusFilter]);

    const statusCounts = useMemo(() => {
        const c: Record<string, number> = {};
        groups.forEach(g => { const s = g.status || 'NOT_STARTED'; c[s] = (c[s] || 0) + 1; });
        return c;
    }, [groups]);

    const totals = useMemo(() => ({
        amount: groups.reduce((s, g) => s + g.totalAmount, 0),
        validated: groups.reduce((s, g) => s + g.totalValidated, 0),
        count: allRows.length,
        confirmedTitles: allRows.filter(r => r.status === 'CONFIRMED').length,
        uniqueDebtors: groups.length,
        validatedDebtors: groups.filter(g => g.status === 'FULLY_VALIDATED' || g.status === 'PARTIALLY_VALIDATED').length,
    }), [allRows, groups]);

    const toggleExpand = (key: string) => {
        setExpanded(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const columns = [
        { id: 'debtor', title: 'Sacado', field: 'debtor', canSort: true },
        { id: 'doc', title: 'CPF/CNPJ', field: 'doc', canSort: true, width: '150px' },
        { id: 'titles', title: 'Títulos', field: 'titles', canSort: true, cssClass: 'text-center', width: '70px' },
        { id: 'amount', title: 'Valor NF', field: 'amount', canSort: true, cssClass: 'text-right', width: '130px' },
        { id: 'validated', title: 'Valor Validado', field: 'validated', canSort: true, cssClass: 'text-right', width: '130px' },
        { id: 'status', title: 'Status', field: 'status', canSort: true, cssClass: 'text-center', width: '160px' },
        { id: 'actions', title: '', canSort: false, width: '40px', cssClass: 'text-center' },
    ];

    const filterGroups = [
        {
            title: '',
            children: (
                <div className="space-y-4">
                    <button onClick={() => setStatusFilter('')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!statusFilter ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                            }`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{groups.length}</span>
                        </div>
                    </button>
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">STATUS</div>
                        <div className="space-y-1">
                            {Object.entries(statusCounts).sort().map(([s, count]) => (
                                <button key={s} onClick={() => setStatusFilter(s)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${statusFilter === s ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                                        }`}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className={`w-3 h-3 rounded-full ${(DV_STATUS_COLORS[s] || '').split(' ')[0]}`} />
                                            <span>{DV_STATUS_LABELS[s] || s}</span>
                                        </div>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{count}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 px-3">RESUMO</div>
                        <div className="px-3 space-y-3">
                            {[
                                { label: 'Sacados', total: totals.uniqueDebtors, validated: totals.validatedDebtors },
                                { label: 'Títulos', total: totals.count, validated: totals.confirmedTitles },
                            ].map(({ label, total, validated }) => {
                                const pct = total > 0 ? Math.round((validated / total) * 100) : 0;
                                return (
                                    <div key={label} className="bg-gray-50 dark:bg-slate-800 rounded-lg p-2.5">
                                        <div className="flex justify-between text-sm mb-1.5">
                                            <span className="text-gray-500">{label}</span>
                                            <span className="dark:text-white font-medium">{total}</span>
                                        </div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-gray-400 text-xs">Validados</span>
                                            <span className="text-green-600 font-medium">{validated}</span>
                                        </div>
                                        <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-1.5">
                                            <div className="bg-green-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                                        </div>
                                        <div className="text-right text-xs text-green-600 mt-0.5">{pct}%</div>
                                    </div>
                                );
                            })}
                            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-2.5">
                                <div className="flex justify-between text-sm mb-1.5">
                                    <span className="text-gray-500">Valor Total</span>
                                    <span className="dark:text-white font-medium">{formatCurrency(totals.amount)}</span>
                                </div>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-400 text-xs">Validados</span>
                                    <span className="text-green-600 font-medium">{formatCurrency(totals.validated)}</span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-1.5">
                                    <div className="bg-green-500 h-1.5 rounded-full transition-all" style={{ width: `${totals.amount > 0 ? Math.round((totals.validated / totals.amount) * 100) : 0}%` }} />
                                </div>
                                <div className="text-right text-xs text-green-600 mt-0.5">{totals.amount > 0 ? Math.round((totals.validated / totals.amount) * 100) : 0}%</div>
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    // Build rows with expand/collapse
    const tableRows: JSX.Element[] = [];
    filteredGroups.forEach((g, gIdx) => {
        const isExpanded = expanded.has(g.key);
        const groupStatusColor = DV_STATUS_COLORS[g.status] || 'bg-gray-100 text-gray-600';

        // Group header row
        tableRows.push(
            <tr key={g.key}
                className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${gIdx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'} border-b border-gray-200 dark:border-slate-700`}
                onClick={() => toggleExpand(g.key)}>
                <td className="py-2 px-3 text-sm font-medium dark:text-white">
                    <div className="flex items-center gap-2">
                        <button className="text-gray-500 hover:text-gray-800 dark:text-gray-400">
                            {isExpanded ? <FaMinus size={10} /> : <FaPlus size={10} />}
                        </button>
                        <span>{g.name}</span>
                        <NewTooltip content={({NONE:'Não iniciado',NAO_INICIADO:'Não iniciado',FAST:'Rápido',WAITING:'Aguardando retorno',MODERATELY_FAST:'Moderadamente rápido',LONG_TIME:'Demora responder',NO_RESPOND:'Não responde'})[g.contactStatus || 'NONE'] || 'Não iniciado'} alignment="top">
                            <span className={`inline-block w-2 h-2 rounded-full ${({NONE:'bg-gray-500',NAO_INICIADO:'bg-gray-500',FAST:'bg-green-500',WAITING:'bg-purple-500',MODERATELY_FAST:'bg-yellow-500',LONG_TIME:'bg-orange-500',NO_RESPOND:'bg-red-500'})[g.contactStatus || 'NONE'] || 'bg-gray-500'}`} />
                        </NewTooltip>
                    </div>
                </td>
                <td className="py-2 px-3 text-sm dark:text-white whitespace-nowrap">{formatCpfCnpj(g.document)}</td>
                <td className="py-2 px-3 text-center text-sm dark:text-white">{g.titleCount}</td>
                <td className="py-2 px-3 text-right text-sm dark:text-white">{formatCurrency(g.totalAmount)}</td>
                <td className="py-2 px-3 text-right text-sm dark:text-white">{formatCurrency(g.totalValidated || 0)}</td>
                <td className="py-2 px-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${groupStatusColor}`}>{DV_STATUS_LABELS[g.status] || g.status}</span>
                </td>
                <td className="py-2 px-3 text-center" onClick={e => e.stopPropagation()}>
                    <NewTooltip content="Validar ou editar validação" alignment="left">
                        <button onClick={() => setSelectedDebtor(g)}
                            className="opacity-0 group-hover:opacity-100 text-blue-600 hover:text-blue-700 dark:text-blue-400 transition-opacity">
                            <FiEdit2 size={14} />
                        </button>
                    </NewTooltip>
                </td>
            </tr>
        );

        // Detail rows
        if (isExpanded) {
            g.items.forEach((r: any, rIdx: number) => (
                tableRows.push(
                    <tr key={`${g.key}-${r.id}`} className="bg-blue-50/50 dark:bg-blue-900/20 border-b border-gray-100 dark:border-slate-700">
                        <td className="py-1.5 px-3 pl-10 text-xs text-gray-700 dark:text-gray-300"
                            title={`Cedente: ${r.assignorName || ''}\nNFe: ${r.nfeKey}\nParcela: ${r.receivableNumber}`}>
                            <span className="text-blue-500">└─</span> {r.titleNumber}
                            {r.status === 'NOT_CONFIRMED' && r.observation && <span className="ml-1 text-red-400 text-[10px]">({r.observation})</span>}
                        </td>
                        <td className="py-1.5 px-3 text-xs text-gray-500 dark:text-gray-400">{r.city}{r.state ? ` - ${r.state}` : ''}</td>
                        <td className="py-1.5 px-3 text-center text-xs text-gray-500 dark:text-gray-400">{formatDate(r.issueDate)}</td>
                        <td className="py-1.5 px-3 text-right text-xs dark:text-gray-300">{formatCurrency(r.amount)}</td>
                        <td className="py-1.5 px-3 text-right text-xs dark:text-gray-300">{formatDate(r.dueDate)}</td>
                        <td className="py-1.5 px-3 text-center">
                            {r.status && r.status !== 'PENDING' && (
                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${r.status === 'CONFIRMED' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {r.status === 'CONFIRMED' ? 'Confirmado' : 'Não confirmado'}
                                </span>
                            )}
                        </td>
                        <td></td>
                    </tr>
                )
            ));
        }
    });

    return (
        <>
            {isLoading && <Loading />}
            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiShield className="text-blue-600" size={18} />
                            <span>Garantias</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {apiInfo.version && <div>v{apiInfo.version}</div>}
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || '').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={initialLoading ? [] : filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={!!selectedDebtor}
                onCloseRightPanel={() => setSelectedDebtor(null)}
                rightPanel={selectedDebtor ? (
                    <DebtorContactModal
                        show={true}
                        onClose={() => setSelectedDebtor(null)}
                        analysisId={validationId || ''}
                        contactId={selectedDebtor.contactId}
                        debtorName={selectedDebtor.name}
                        debtorDocument={selectedDebtor.document}
                        debtorStatus={selectedDebtor.validationStatus}
                        contactStatus={selectedDebtor.contactStatus}
                        receivables={selectedDebtor.items || []}
                        operationCode={apiData?.operationCode || ''}
                        history={selectedDebtor.history || []}
                        onRefresh={() => setRefreshKey(k => k + 1)}
                    />
                ) : null}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <button onClick={() => navigate(COLLATERAL_ROUTES.validation)}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                                    <IoMdArrowRoundBack size={18} />
                                </button>
                                <div>
                                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Recebíveis</h1>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{apiData?.operationCode} — {apiData?.assignor}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button onClick={() => setRefreshKey(k => k + 1)}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                                    <IoIosRefresh size={18} />
                                </button>
                                <div className="w-80">
                                    <TextEdit label="Buscar sacado ou CPF/CNPJ..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => e.key === 'Enter' && setSearch(searchInput)} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                        <ATable headers={columns} fieldSorted={sortField} orderSorted={sortOrder} onSort={(f, o) => { setSortField(f); setSortOrder(o); }}>
                            {tableRows.length === 0 ? (
                                <tr><td colSpan={7} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhum recebível encontrado</td></tr>
                            ) : tableRows}
                        </ATable>
                        )}
                    </div>

                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={0} totalPages={1} itemsPerPage={100} totalElements={filteredGroups.length} onPageChange={() => { }} itemsLabel="sacados" />
                    </div>
                </div>
            </ThreeColumnLayout>

            <Outlet context={{ onDataChange: () => setRefreshKey(k => k + 1) }} />
        </>
    );
};

export default ReceivablePageList;
